import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setCenter, setCountry } from '../../../redux/slices/centerSlice';
import { setCampCenters, setLoading, setError } from '../../../redux/slices/centersListSlice';
import campService from '../services/campService';

const CampCenter = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [centers, setCenters] = useState([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [localError, setLocalError] = useState(null);
  const [countryId, setCountryId] = useState('USA'); // Default to USA
  const hasInitialized = useRef(false);

  // Map country ID to description
  const countryMap = {
    '1': 'USA',
    '2': 'Canada',
    'USA': 'USA',
    'Canada': 'Canada'
  };

  // Fetch centers when countryId changes
  useEffect(() => {
    // Skip the initial render in StrictMode
    if (!hasInitialized.current && countryId === 'USA') {
      hasInitialized.current = true;
      return;
    }

    const fetchCenters = async () => {
      try {
        setLocalLoading(true);
        dispatch(setLoading(true));
        const countryDesc = countryMap[countryId] || 'USA';
        const data = await campService.getCenters(countryDesc);
        setCenters(data || []);
        // Save to Redux store for use in other components
        dispatch(setCampCenters(data || []));
        setLocalError(null);
        dispatch(setError(null));
      } catch (err) {
        const errorMsg = 'Failed to load centers. Please try again later.';
        setLocalError(errorMsg);
        dispatch(setError(errorMsg));
        console.error(err);
      } finally {
        setLocalLoading(false);
        dispatch(setLoading(false));
      }
    };

    fetchCenters();
  }, [countryId, dispatch]);

  const handleSelectCenter = (center) => {
    // Store center data in Redux
    dispatch(setCenter({
      center_Id: center.center_Id,
      center_Name: center.center_Name,
      center_Desc: center.center_Desc,
      center_Logo_URL: center.center_Logo_URL,
      country_Desc: countryId,
    }));
    
    // Store country in Redux
    dispatch(setCountry(countryId));
    
    // Navigate to selection page
    navigate(`/camp/selection?centerId=${center.center_Id}&country=${countryId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-16">
      <div className="container-custom">
        {/* Header */}
        <div className="mb-16 text-center">
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
              🏕️ Camp Experience
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
            Select Your Camp Center
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Discover amazing camps at our premium centers across the country
          </p>
        </div>

        {/* Country Selector */}
        <div className="mb-12 max-w-md mx-auto">
          <label className="block text-gray-700 font-bold mb-3 text-lg">Select Country</label>
          <select
            value={countryId}
            onChange={(e) => setCountryId(e.target.value)}
            className="w-full px-6 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 transition bg-white font-semibold text-gray-700"
          >
            <option value="USA">🇺🇸 USA</option>
            <option value="Canada">🇨🇦 Canada</option>
          </select>
        </div>

        {/* Loading State */}
        {localLoading && (
          <div className="text-center py-12">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <p className="mt-4 text-gray-600">Loading centers...</p>
          </div>
        )}

        {/* Error State */}
        {localError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{localError}</p>
          </div>
        )}

        {/* Centers Grid */}
        {!localLoading && centers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {centers.map((center) => (
              <div
                key={center.center_Id}
                className="item-card cursor-pointer"
                onClick={() => handleSelectCenter(center)}
              >
                {/* Image Section */}
                {center.center_Logo_URL && (
                  <div className="h-56 bg-gradient-to-br from-blue-400 to-purple-400 overflow-hidden relative rounded-t-[18px]">
                    <img
                      src={center.center_Logo_URL?.trim()}
                      alt={center.center_Name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x300?text=Camp+Center';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                )}
                
                {/* Content Section */}
                <div className="p-8 relative z-10">
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">
                    {center.center_Name}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3 text-sm leading-relaxed">
                    {center.center_Desc}
                  </p>
                  
                  {center.camp_Count && (
                    <div className="mb-6 inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold">
                      ⛺ {center.camp_Count} camp{center.camp_Count !== 1 ? 's' : ''} available
                    </div>
                  )}
                  
                  <button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 px-4 rounded-xl transition transform hover:shadow-lg">
                    Explore Camps →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!localLoading && centers.length === 0 && !localError && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No centers available for the selected country.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampCenter;
